import { useEffect, useRef, useState } from 'react';
import { endpoints } from '../../api';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { studioPrefill } from '../studio/studio-prefill';

/** Ключ черновика «Повтори движения»: тот же у родной формы и у экрана на движке (dance-server.ts) — симметрия отката */
export const motionDraftKey = (owner: number, app: number) => `steos:motion:draft:${owner}:${app}`;

/** Редактируемые поля храним отдельно от отправленной заявки, по владельцу и режиму. */
export function useMotionDraft(state: CircleFormState, seeded: boolean) {
  const { ownerId, form, inputs, intent, setInputs, setUploadIds } = state;
  const key = ownerId && form ? motionDraftKey(ownerId, form.app_id) : '';
  const ready = useRef('');
  const [settled, setSettled] = useState('');
  const [badKey, setBadKey] = useState('');
  const [unavailableKey, setUnavailableKey] = useState('');

  useEffect(() => {
    if (!key || !form) return;
    // Неизвестный исход POST восстанавливается только механизмом intent с прежним ключом.
    if (intent) {
      ready.current = key;
      let active = true;
      void Promise.resolve().then(() => {
        if (active) setSettled(key);
      });
      return () => {
        active = false;
      };
    }
    if (ready.current === key) return;
    const controller = new AbortController();
    async function restore() {
      try {
        const raw = localStorage.getItem(key);
        if (raw && !seeded) {
          const saved = JSON.parse(raw);
          if (
            saved?.owner !== ownerId ||
            saved?.app !== form!.app_id ||
            !saved.inputs ||
            typeof saved.inputs !== 'object' ||
            Array.isArray(saved.inputs) ||
            Object.entries(saved.inputs).some(([k, v]) => !/^[a-z][a-z0-9_]{0,63}$/.test(k) || typeof v !== 'string' || v.length > 20000)
          )
            throw Error('invalid_draft');
          // Проверяем файлы через существующий API; временные ссылки и содержимое файлов не сохраняем.
          const next = await studioPrefill(form!, saved.inputs, null, controller.signal);
          if ((await endpoints.me()).user.id !== ownerId) throw Error('owner_changed');
          if (controller.signal.aborted) return;
          setInputs(next.inputs);
          setUploadIds(next.uploadIds);
          if (next.unavailableFile) setUnavailableKey(key);
        }
        if (!controller.signal.aborted) ready.current = key;
      } catch {
        if (!controller.signal.aborted) setBadKey(key);
      } finally {
        if (!controller.signal.aborted) setSettled(key);
      }
    }
    void restore();
    return () => controller.abort();
  }, [key, form, ownerId, intent, seeded, setInputs, setUploadIds]);

  useEffect(() => {
    if (!key || intent || ready.current !== key || settled !== key || badKey === key) return;
    let active = true;
    try {
      // Без таймера: переход в другой экран не должен терять последнюю правку. Согласие даётся заново.
      const safe = Object.fromEntries(Object.entries(inputs).filter(([field]) => field !== 'consent_accepted'));
      localStorage.setItem(key, JSON.stringify({ owner: ownerId, app: form!.app_id, inputs: safe }));
    } catch {
      void Promise.resolve().then(() => {
        if (active) setBadKey(key);
      });
    }
    return () => {
      active = false;
    };
  }, [key, inputs, intent, ownerId, form, settled, badKey]);

  async function reset() {
    try {
      if ((await endpoints.me()).user.id !== ownerId) return;
      localStorage.removeItem(key);
      ready.current = key;
      setSettled(key);
      setBadKey('');
      setUnavailableKey('');
    } catch {
      setBadKey(key);
    }
  }
  return {
    blocked: !intent && !!key && badKey === key,
    restoring: !intent && !!key && settled !== key,
    unavailableFile: unavailableKey === key && !!key,
    reset,
  };
}
